import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { Plus, Signal, Trash2 } from 'lucide-react';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Card from '../Card';
import { CardHeader } from '../Card/styles';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import {
  AddAddressButton,
  EditLatencyRow,
  LatencyRow,
  LatencySkeleton,
  LineChart,
  NoAddress,
} from './styles';
import { LatencyIcon } from '../IconPicker/styles';
import { getInitials } from '@/utils/stringUtils';
import { DynamicIcon, type IconName } from 'lucide-react/dynamic';
import IconPicker from '../IconPicker';
import TextField from '../Input/TextField';

type LatencyFieldErrorsType = Record<
  number,
  { name?: string; address?: string }
>;

const MAX_ADDRESSES = 5;

type PingAddressWithLatency = PingAddressType & { latency?: string };

type LatencyCardProps = {
  dataLatency: PingAddressType[];
  editMode?: boolean;
  onChangeAddresses?: (addresses: PingAddressType[]) => void;
  errors?: LatencyFieldErrorsType;
};

function LatencyCard({
  dataLatency,
  editMode = false,
  onChangeAddresses,
  errors,
}: LatencyCardProps) {
  const { t } = useTranslation();
  const [latencies, setLatencies] =
    useState<PingAddressWithLatency[]>(dataLatency);
  const [loading, setLoading] = useState(true);
  const [pingHistory, setPingHistory] = useState<Record<string, number[]>>({});

  const theme = useTheme();

  useEffect(() => {
    setLatencies(dataLatency);
  }, [dataLatency]);

  useEffect(() => {
    // If in edit mode, do not open EventSource connections
    if (editMode) return;

    const sources: EventSource[] = [];

    function openConnections() {
      dataLatency.forEach((item, index) => {
        const eventSource = new EventSource(`/api/notify/ping/${item.address}`);
        setLoading(false);
        eventSource.addEventListener('Ping', (event) => {
          const data = JSON.parse(event.data);
          const numericLatency =
            parseFloat(data.latency.replace('ms', '').trim()) || 0;

          setLatencies((prev) =>
            prev.map((l, i) =>
              i === index ? { ...l, latency: data.latency } : l
            )
          );

          setPingHistory((prev) => {
            const currentHistory = prev[item.address] || [0, 0, 0, 0, 0];
            const newHistory = [...currentHistory, numericLatency].slice(-10);
            return { ...prev, [item.address]: newHistory };
          });

          setLatencies((prev) =>
            prev.map((l, i) =>
              i === index ? { ...l, latency: data.latency } : l
            )
          );
        });

        eventSource.onerror = () => {
          eventSource.close();
        };

        sources.push(eventSource);
      });
    }

    function closeConnections() {
      sources.forEach((source) => source.close());
    }

    openConnections();
    window.addEventListener('pagehide', closeConnections);

    return () => {
      closeConnections();
      window.removeEventListener('pagehide', closeConnections);
    };
  }, [dataLatency, editMode]);

  function evaluateLatency(value: string) {
    let color = '#9E9E9E';
    let display = String(value);

    const parsed = parseFloat(display.replace('ms', '').trim());

    if (!isNaN(parsed)) {
      if (parsed <= 30 && parsed >= 0) {
        color = '#4CAF50';
      } else if (parsed <= 100 && parsed > 30) {
        color = '#FF9800';
      } else {
        color = '#F44336';
      }
      display = `${parsed} ms`;
    }

    return color;
  }

  function updateAddresses(
    updater: (prev: PingAddressWithLatency[]) => PingAddressWithLatency[]
  ) {
    const updated = updater(latencies);
    setLatencies(updated);
    onChangeAddresses?.(
      updated.map(({ name, address, icon }) => ({ name, address, icon }))
    );
  }

  const handleFieldChange = (
    index: number,
    field: 'name' | 'address',
    value: string
  ) => {
    updateAddresses((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleIconSelect = (index: number, icon?: IconName) => {
    updateAddresses((prev) =>
      prev.map((item, i) => (i === index ? { ...item, icon } : item))
    );
  };

  // add blank address
  const handleAddAddress = () => {
    if (latencies.length >= MAX_ADDRESSES) return;
    updateAddresses((prev) => [...prev, { name: '', address: '' }]);
  };

  // remove selected address
  const handleRemoveAddress = (index: number) => {
    updateAddresses((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Card sx={{ paddingBottom: '26px' }}>
      {/** HEADER */}
      <CardHeader sx={{ maxHeight: '40px' }}>
        <Signal />
        <Typography variant="h5" fontWeight={500}>
          {t('dashboard.latency')}
        </Typography>
        {/** add new address */}
        {editMode ? (
          <AddAddressButton
            variant="outlined"
            startIcon={<Plus size={18} />}
            onClick={handleAddAddress}
            disabled={latencies.length >= MAX_ADDRESSES}
          >
            {t('dashboard.latencyEdit.addAddress')}
          </AddAddressButton>
        ) : (
          <Typography variant="caption" fontSize="italic !important">
            {t('dashboard.latencyUpdated', { minutes: 30 })}
          </Typography>
        )}
      </CardHeader>

      {/** if on edit mode renders a set of text fields */}
      {editMode ? (
        <Stack gap="16px" width="100%">
          {latencies.length === 0 && (
            <NoAddress>
              <Typography variant="h6" color={theme.palette.grey['01']}>
                {t('dashboard.latencyEdit.noAddresses')}
              </Typography>
            </NoAddress>
          )}
          {latencies.map((item, index) => (
            <EditLatencyRow key={index}>
              <IconPicker
                icon={item.icon}
                initials={getInitials(item.name)}
                editable
                onSelect={(icon) => handleIconSelect(index, icon)}
              />

              {/** Name */}
              <TextField
                label={t('dashboard.latencyEdit.name')}
                value={item.name}
                onChange={(e) =>
                  handleFieldChange(index, 'name', e.target.value)
                }
                width="200px"
                error={!!errors?.[index]?.name}
              />

              {/** Address */}
              <TextField
                label={t('dashboard.latencyEdit.address')}
                value={item.address}
                onChange={(e) =>
                  handleFieldChange(index, 'address', e.target.value)
                }
                width="100%"
                error={!!errors?.[index]?.address}
              />

              {/** Remove button */}
              <IconButton
                color="error"
                onClick={() => handleRemoveAddress(index)}
              >
                <Trash2 size={18} />
              </IconButton>
            </EditLatencyRow>
          ))}
          {latencies.length >= MAX_ADDRESSES && (
            <Typography variant="caption" color={theme.palette.grey['01']}>
              {t('dashboard.latencyEdit.maxReached')}
            </Typography>
          )}
        </Stack>
      ) : loading ? (
        <LatencySkeleton variant="rounded" animation="wave" />
      ) : (
        <Stack gap="24px" width="100%">
          {latencies.map((item, key) => (
            <LatencyRow key={key}>
              <Stack direction="row" alignItems="center" gap="16px">
                {/** icon */}
                <Stack>
                  <LatencyIcon>
                    {item.icon ? (
                      <DynamicIcon name={item.icon} size={20} />
                    ) : (
                      getInitials(item.name)
                    )}
                  </LatencyIcon>
                </Stack>

                {/** Name */}
                <Stack direction="row" gap="16px" alignItems="flex-end">
                  <Typography
                    variant="h5"
                    color={theme.palette.white['06']}
                    justifyContent="flex-start"
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={theme.palette.grey['01']}
                  >
                    {item.address}
                  </Typography>
                </Stack>
              </Stack>

              {/** Latency */}
              <Stack direction="row" alignItems="center">
                <Typography color={evaluateLatency(item.latency!)}>
                  {item.latency}
                </Typography>
                <LineChart
                  data={pingHistory[item.address] || [0, 0, 0, 0, 0]}
                  area
                  color={evaluateLatency(item.latency!)}
                  showHighlight={false}
                  showTooltip={false}
                />
              </Stack>
            </LatencyRow>
          ))}
        </Stack>
      )}
    </Card>
  );
}

export default LatencyCard;
